import { useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CONVERSATION_STATE, CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import { getAuthConfig, getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../../data/store'
import { useActiveEpisodeForSelectedClient } from '../../../episodes/data/hooks/useActiveEpisodeForSelectedClient'
import { useCreateConversationMutation } from '../messagesApi'
import { upsertResolvedConversation } from '../messagesSlice'
import { MessengerParticipant, MessengerThread } from '../types'

// Threads are eligible before their conversation exists (the guardian thread, and any client this
// provider isn't primary for), so resolve the id and create it on first send. Null on refusal or
// failure. The caller surfaces that.
export const useEnsureConversation = () => {
  const providerId = useSelector(getAuthUserId)
  const clientId = useSelector(getSelectedClientId)
  const openClientAccessSupported = useSelector(getAuthConfig)?.openClientAccessSupported ?? false
  const { isDBTTeensEnabled } = useFlags()
  // Patient-scoped: the provider-scoped endpoint returns nothing for a non-primary provider, which
  // refused every send for them.
  const { activeEpisode } = useActiveEpisodeForSelectedClient()
  const episodeId = activeEpisode?.id
  const [createConversation] = useCreateConversationMutation()
  const dispatch = useAppDispatch()
  const shouldRetainResolvedConversation =
    openClientAccessSupported &&
    isDBTTeensEnabled &&
    activeEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens &&
    activeEpisode.provider_id !== providerId
  // Keyed by client + stable thread id so one thread's create is never reused by another. A resolved id
  // stays memoized so a send during a stale conversation-list response still reuses it. Only failures
  // are evicted.
  const pendingCreates = useRef(new Map<string, Promise<string | null>>())

  return useCallback(
    async (thread: MessengerThread | null): Promise<string | null> => {
      if (thread?.conversationId) {
        return thread.conversationId
      }
      // Pair threads are created through useEnsureProviderPairConversationForThread instead.
      if (
        !thread ||
        thread.participant === MessengerParticipant.PROVIDER_PAIR ||
        !thread.canSendMessages ||
        !thread.recipientId ||
        !clientId ||
        !episodeId
      ) {
        return null
      }
      const key = `${clientId}:${thread.id}`
      const pending = pendingCreates.current.get(key)
      if (pending) {
        return pending
      }
      const create = createConversation({
        episodeId,
        participants: [
          { participant_lyra_id: providerId, type: 'provider' },
          // The recipient always takes the patient slot: for a guardian thread that makes
          // patient_lyra_id the guardian's, which is how getThreadParticipant classifies it later.
          { participant_lyra_id: thread.recipientId, type: 'patient' },
        ],
      })
        .unwrap()
        .then((conversation) => {
          // The messaging id, not the db `id`. /conversations/{id}/messages takes this one.
          const conversationId = conversation.external_conversation_id
          const now = new Date().toISOString()

          if (shouldRetainResolvedConversation) {
            dispatch(
              upsertResolvedConversation({
                conversation_attributes: {
                  episode_id: conversation.episode_id ?? episodeId,
                  type: conversation.type ?? CONVERSATION_TYPE.PROVIDER,
                },
                episode_ids: [conversation.episode_id ?? episodeId],
                conversation_date_created: now,
                conversation_id: conversationId,
                db_conversation_id: conversation.id,
                conversation_state: conversation.status ?? CONVERSATION_STATE.ACTIVE,
                conversation_update_datetime: now,
                anchor_patient_lyra_id: clientId,
                patient_lyra_id: thread.recipientId,
                provider_lyra_id: providerId,
                conversation_last_message_sent_datetime: null,
                type: conversation.type ?? CONVERSATION_TYPE.PROVIDER,
              }),
            )
          }
          return conversationId
        })
        .catch(() => {
          pendingCreates.current.delete(key)
          return null
        })
      pendingCreates.current.set(key, create)
      return create
    },
    [clientId, episodeId, providerId, createConversation, dispatch, shouldRetainResolvedConversation],
  )
}
